/**
 * Invite View Page
 *
 * Detail view for a single invite.
 *
 * @fileoverview Admin invite view page
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Card,
  CardBody,
  SimpleGrid,
  Skeleton,
  useToast,
  useDisclosure,
  useColorModeValue,
  Code,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { MdArrowBack, MdChevronRight, MdRefresh, MdCancel, MdDelete, MdContentCopy } from 'react-icons/md';
import { Link as RouterLink } from 'react-router-dom';
import { useAdminInvite } from '../../../hooks/useAdminInvites';
import { RevokeInviteModal, DeleteInviteModal } from '../../../components/admin';

/**
 * Get status badge for invite
 */
function getStatusBadge(invite: { status: boolean; used: boolean; expiresAt: string }) {
  if (invite.used) {
    return { colorScheme: 'green', label: 'Used' };
  }
  if (!invite.status) {
    return { colorScheme: 'red', label: 'Revoked' };
  }
  if (new Date(invite.expiresAt) < new Date()) {
    return { colorScheme: 'orange', label: 'Expired' };
  }
  return { colorScheme: 'blue', label: 'Active' };
}

/**
 * Invite View Page Component
 *
 * Page for viewing invite details.
 */
export const InviteView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const revokeModal = useDisclosure();
  const deleteModal = useDisclosure();

  const { invite, loading, error, revokeInvite, resendInvite, deleteInvite, refetch } = useAdminInvite(id);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.500', 'gray.400');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleResend = async () => {
    const result = await resendInvite();
    if (result) {
      toast({
        title: 'Invite resent',
        description: `A new invite email has been sent to ${invite?.email}.`,
        status: 'success',
        duration: 3000,
      });
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't resend the invite. Please try again.",
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleRevokeConfirm = async () => {
    const result = await revokeInvite();
    if (result) {
      toast({
        title: 'Invite revoked',
        description: `The invite for ${invite?.email} has been revoked.`,
        status: 'warning',
        duration: 3000,
      });
      revokeModal.onClose();
      refetch();
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't revoke the invite. Please try again.",
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDeleteConfirm = async () => {
    const success = await deleteInvite();
    if (success) {
      toast({
        title: 'Invite deleted',
        description: 'The invite has been deleted.',
        status: 'success',
        duration: 3000,
      });
      navigate('/admin/invites');
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't delete the invite. Please try again.",
        status: 'error',
        duration: 5000,
      });
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: `${label} copied to clipboard`,
      status: 'info',
      duration: 2000,
    });
  };

  // Loading state
  if (loading) {
    return (
      <VStack spacing={6} align="stretch">
        <Skeleton h="24px" w="200px" />
        <Skeleton h="32px" w="300px" />
        <Skeleton h="200px" />
      </VStack>
    );
  }

  // Error state
  if (error || !invite) {
    return (
      <VStack spacing={6} align="stretch">
        <Button
          variant="ghost"
          leftIcon={<Icon as={MdArrowBack} />}
          onClick={() => navigate('/admin/invites')}
          alignSelf="flex-start"
        >
          Back to Invites
        </Button>
        <Box textAlign="center" py={10}>
          <Heading size="md" color="red.500">
            {error || 'Invite not found'}
          </Heading>
        </Box>
      </VStack>
    );
  }

  const status = getStatusBadge(invite);
  const canResend = !invite.used;
  const canRevoke = invite.status && !invite.used;
  const signupUrl = `${window.location.origin}/signup?token=${invite.token}`;

  return (
    <VStack spacing={6} align="stretch">
      {/* Breadcrumb */}
      <Breadcrumb separator={<Icon as={MdChevronRight} color="gray.400" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/admin/invites">
            Invites
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>{invite.email}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Header */}
      <HStack justify="space-between" wrap="wrap" gap={4}>
        <Box>
          <HStack spacing={3} align="center">
            <Heading size="lg">{invite.email}</Heading>
            <Badge colorScheme={status.colorScheme} fontSize="sm">
              {status.label}
            </Badge>
          </HStack>
          <Text color="gray.500" mt={1}>
            Invited on {formatDate(invite.createdAt)}
          </Text>
        </Box>
        <HStack spacing={2}>
          {canResend && (
            <Button
              leftIcon={<Icon as={MdRefresh} />}
              colorScheme="blue"
              variant="outline"
              onClick={handleResend}
            >
              Resend
            </Button>
          )}
          {canRevoke && (
            <Button
              leftIcon={<Icon as={MdCancel} />}
              colorScheme="orange"
              variant="outline"
              onClick={revokeModal.onOpen}
            >
              Revoke
            </Button>
          )}
          <Button
            leftIcon={<Icon as={MdDelete} />}
            colorScheme="red"
            variant="ghost"
            onClick={deleteModal.onOpen}
          >
            Delete
          </Button>
        </HStack>
      </HStack>

      {/* Details Card */}
      <Card bg={bgColor} border="1px" borderColor={borderColor}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color={labelColor} mb={1}>Email</Text>
              <Text fontWeight="medium">{invite.email}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color={labelColor} mb={1}>Status</Text>
              <Badge colorScheme={status.colorScheme}>{status.label}</Badge>
            </Box>
            <Box>
              <Text fontSize="sm" color={labelColor} mb={1}>Sent By</Text>
              <Text fontWeight="medium">
                {invite.sentBy?.displayName || invite.sentBy?.email || 'Unknown'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color={labelColor} mb={1}>Expires</Text>
              <Text fontWeight="medium">{formatDate(invite.expiresAt)}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color={labelColor} mb={1}>Created</Text>
              <Text fontWeight="medium">{formatDate(invite.createdAt)}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color={labelColor} mb={1}>Updated</Text>
              <Text fontWeight="medium">{formatDate(invite.updatedAt)}</Text>
            </Box>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Signup URL */}
      {!invite.used && invite.status && (
        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardBody>
            <Text fontSize="sm" color={labelColor} mb={2}>Signup URL</Text>
            <HStack>
              <Code p={2} borderRadius="md" flex={1} fontSize="sm" overflow="hidden" textOverflow="ellipsis">
                {signupUrl}
              </Code>
              <Tooltip label="Copy to clipboard">
                <IconButton
                  aria-label="Copy URL"
                  icon={<Icon as={MdContentCopy} />}
                  size="sm"
                  onClick={() => copyToClipboard(signupUrl, 'Signup URL')}
                />
              </Tooltip>
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* Token */}
      <Card bg={bgColor} border="1px" borderColor={borderColor}>
        <CardBody>
          <Text fontSize="sm" color={labelColor} mb={2}>Token</Text>
          <HStack>
            <Code p={2} borderRadius="md" flex={1} fontSize="sm">
              {invite.token}
            </Code>
            <Tooltip label="Copy to clipboard">
              <IconButton
                aria-label="Copy token"
                icon={<Icon as={MdContentCopy} />}
                size="sm"
                onClick={() => copyToClipboard(invite.token, 'Token')}
              />
            </Tooltip>
          </HStack>
        </CardBody>
      </Card>

      {/* Back Button */}
      <Button
        variant="ghost"
        leftIcon={<Icon as={MdArrowBack} />}
        onClick={() => navigate('/admin/invites')}
        alignSelf="flex-start"
      >
        Back to Invites
      </Button>

      {/* Modals */}
      <RevokeInviteModal
        invite={invite}
        isOpen={revokeModal.isOpen}
        onClose={revokeModal.onClose}
        onConfirm={handleRevokeConfirm}
        loading={loading}
      />
      <DeleteInviteModal
        invite={invite}
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        onConfirm={handleDeleteConfirm}
        loading={loading}
      />
    </VStack>
  );
};

export default InviteView;
